import { useId, useState, type KeyboardEvent } from "react";

import type { ReadingReviewOut, ResolutionHow } from "@/api";
import { Button } from "@/components/Button";
import { IconButton } from "@/components/IconButton";
import { CheckIcon, UndoIcon } from "@/components/icons";
import { formatMm } from "@/lib/format";
import { LENGTH_REFUSALS, parseLengthMm } from "@/lib/length";
import type { PageImage } from "@/lib/usePageImage";

import { reasonSentence, settled, stateWord, type ReviewRow } from "./model";
import styles from "./Review.module.css";
import { WallExcerpt } from "./WallExcerpt";

interface WallRowProps {
  row: ReviewRow;
  /** The full row — question, excerpt, choices, field — rather than one line. */
  open: boolean;
  /** Opened by „Ändern“ on a row that already carries a figure. */
  editing: boolean;
  active: boolean;
  /**
   * How often this wall's state changed since the screen opened. Each change
   * lets the row settle in once — a figure set, a ring that closed — while
   * the arrival itself stands still.
   */
  settle: number;
  image: PageImage;
  /** The page size the reading names — the excerpt's frame. */
  page?: Pick<ReadingReviewOut, "pageWidthPx" | "pageHeightPx"> | null;
  /** What is wrong with this wall's value, from the field or the server. */
  error: string | null;
  onError: (message: string | null) => void;
  onResolve: (valueMm: number, how: ResolutionHow, jump: boolean) => void;
  onUndo: () => void;
  onEdit: () => void;
  onCancel: () => void;
  /** The pointer over the row, or the focus inside it — the wall lights up in both drawings. */
  onActive: (how: "hover" | "focus", on: boolean) => void;
  fieldRef: (element: HTMLInputElement | null) => void;
  editRef: (element: HTMLButtonElement | null) => void;
}

/**
 * One wall in the list (plan 4 §5).
 *
 * Open, it asks: the plaque, the reason in one sentence, what was read, the
 * person's own handwriting around the wall, the values the readings found as
 * buttons, a field. Settled, it is one line — „✓ W3 · 1.500 mm · von dir“ —
 * and stays where it is, so the list never moves under the cursor.
 *
 * The keys: Enter or a choice sets the value and moves on, a blur with a
 * valid value sets it and stays, Escape puts the field back. ⌘/Strg+Enter
 * and ⌘/Strg+Z belong to the screen.
 */
export function WallRow({
  row,
  open,
  editing,
  active,
  settle,
  image,
  page = null,
  error,
  onError,
  onResolve,
  onUndo,
  onEdit,
  onCancel,
  onActive,
  fieldRef,
  editRef,
}: WallRowProps) {
  const id = useId();
  const { wall } = row;
  const [text, setText] = useState(() => (editing && row.valueMm !== null ? String(row.valueMm) : ""));
  const parsed = parseLengthMm(text);
  const total = wall.candidates.reduce((sum, candidate) => sum + candidate.votes, 0) || 3;

  const activeProps = {
    onPointerEnter: () => onActive("hover", true),
    onPointerLeave: () => onActive("hover", false),
    onFocus: () => onActive("focus", true),
    onBlur: () => onActive("focus", false),
  };

  if (!open) {
    return (
      <li className={styles.compact} data-state={row.state} data-active={active} data-wall={wall.wallId} {...activeProps}>
        <span className={styles.compactMark} aria-hidden="true">
          {settled(row) ? <CheckIcon size={14} /> : "–"}
        </span>
        <span key={settle} className={settle > 0 ? `${styles.compactText} ${styles.settled}` : styles.compactText}>
          <span className={`num ${styles.compactLabel}`}>{wall.label}</span>
          {row.valueMm !== null ? (
            <>
              <span aria-hidden="true"> · </span>
              <span className={`num ${styles.compactValue}`}>{formatMm(row.valueMm)}</span>
            </>
          ) : null}
          <span aria-hidden="true"> · </span>
          <span className={styles.compactWord}>{stateWord(row.state)}</span>
          {row.displacedMm !== null ? (
            <span className={styles.compactDisplaced}>
              <span aria-hidden="true"> · </span>gelesen <span className="num">{formatMm(row.displacedMm)}</span>
            </span>
          ) : row.resolution?.origin === "ringfix" && wall.text ? (
            // Taken from the ring's suggestion (§10.1): what the sheet said
            // stays beside the person's figure — never „geprüft“.
            <span className={styles.compactDisplaced}>
              <span aria-hidden="true"> · </span>auf dem Blatt „<span className="num">{wall.text}</span>“
            </span>
          ) : null}
        </span>
        <span className={styles.compactActions}>
          <Button
            variant="leise"
            ref={editRef}
            onClick={onEdit}
            aria-label={`${row.valueMm === null ? "Maß setzen" : "Ändern"}: ${wall.label}`}
          >
            {row.valueMm === null ? "Maß setzen" : "Ändern"}
          </Button>
          {row.resolution !== null ? (
            <IconButton label={`${wall.label} zurücknehmen`} icon={<UndoIcon />} onClick={onUndo} />
          ) : null}
        </span>
      </li>
    );
  }

  function commit(jump: boolean): void {
    if (text.trim() === "") return;
    if (!parsed.ok) {
      onError(LENGTH_REFUSALS[parsed.reason as Exclude<typeof parsed.reason, "empty">]);
      return;
    }
    onError(null);
    onResolve(parsed.mm, "getippt", jump);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter" && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      if (text.trim() === "") {
        onError(LENGTH_REFUSALS.unreadable);
        return;
      }
      commit(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onError(null);
      if (editing) onCancel();
      else setText("");
    }
  }

  const reasonId = `${id}-reason`;
  const errorId = `${id}-error`;
  const readingId = `${id}-reading`;
  return (
    <li
      className={styles.row}
      data-state={row.state}
      data-active={active}
      data-wall={wall.wallId}
      aria-labelledby={`${id}-plaque ${reasonId}`}
      {...activeProps}
    >
      <div key={settle} className={settle > 0 ? `${styles.rowHead} ${styles.settled}` : styles.rowHead}>
        <span id={`${id}-plaque`} className={`num ${styles.plaque}`} data-state={row.state}>
          {wall.label}
        </span>
        <span className={styles.rowWord}>{stateWord(row.state)}</span>
        <p id={reasonId} className={styles.reason}>
          {settled(row) && row.displacedMm !== null
            ? `${row.displacedChecked ? "Gelesen und geprüft" : "Übereinstimmend gelesen"}: ${formatMm(row.displacedMm)}.`
            : reasonSentence(wall)}
        </p>
      </div>

      {wall.text !== null || image.status !== "failed" ? (
        <figure className={styles.sight}>
          <WallExcerpt wall={wall} image={image} page={page} />
          {wall.text !== null ? (
            <figcaption className={styles.read}>
              gelesen: „<span className="num">{wall.text}</span>“
            </figcaption>
          ) : null}
        </figure>
      ) : null}

      {wall.candidates.length > 0 ? (
        <div className={styles.choices} role="group" aria-label={`Gelesene Werte für ${wall.label}`}>
          {wall.candidates.map((candidate) => (
            <button
              key={candidate.valueMm}
              type="button"
              className={styles.choice}
              aria-label={`${formatMm(candidate.valueMm)} wählen — ${candidate.votes} von ${total} Lesungen`}
              aria-pressed={row.resolution?.valueMm === candidate.valueMm}
              onClick={() => {
                onError(null);
                onResolve(candidate.valueMm, "gewaehlt", true);
              }}
            >
              <span className="num">{formatMm(candidate.valueMm)}</span>
              <span className={styles.votes}>
                <span className="tabular">{candidate.votes}</span> von <span className="tabular">{total}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.entry}>
        <label className="visually-hidden" htmlFor={`${id}-field`}>
          Maß für {wall.label}
        </label>
        <input
          ref={fieldRef}
          id={`${id}-field`}
          className={`num ${styles.input}`}
          data-wall={wall.wallId}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          placeholder="z. B. 5,12 oder 5120"
          value={text}
          aria-invalid={error !== null ? true : undefined}
          aria-describedby={[error !== null ? errorId : null, parsed.ok ? readingId : null, reasonId].filter(Boolean).join(" ")}
          onChange={(event) => {
            setText(event.target.value);
            if (error !== null) onError(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => commit(false)}
        />
        {/* What the field will take, as it is typed — never as a claim
            before the person confirms with Enter or by leaving the field. */}
        <span id={readingId} className={`num ${styles.reading}`} aria-hidden={parsed.ok ? undefined : true}>
          {parsed.ok ? `= ${formatMm(parsed.mm)}` : ""}
        </span>
        {editing ? (
          <Button variant="leise" onClick={onCancel}>
            Abbrechen
          </Button>
        ) : null}
      </div>
      {error !== null ? (
        <p id={errorId} className={styles.error}>
          {error}
        </p>
      ) : null}
    </li>
  );
}
